/**
 * Estrutura da aplicação: navegação lateral, barra superior e área de trabalho.
 *
 * CERES-UX-001 — A NAVEGAÇÃO É POR JORNADA, E NÃO POR MÓDULO.
 *
 * O menu tem cinco jornadas e um "Mais" (ver `navegacao.ts`): Hoje, Pré-vendas,
 * Negócios, Conversas, Relacionamentos. Os demais destinos continuam existindo
 * com a mesma permissão e capacidade, no lançador do "Mais" ou no contexto das
 * jornadas. Itens sem permissão ou capacidade simplesmente não aparecem (o
 * servidor barra de todo modo).
 *
 * MENU RECOLHÍVEL (reforma "Cockpit Executivo", mantido).
 *
 * No desktop a navegação tem dois estados — recolhido (só ícones, 72px) e
 * expandido (ícone, nome e orientação curta, 248px) — com preferência lembrada
 * por usuário e organização (`useMenuColapsado`). Abaixo de `lg` a navegação
 * principal vai para uma barra inferior fixa (as seis entradas, sempre à mão)
 * e a gaveta continua existindo para o menu do usuário e a versão.
 */

import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BloqueioPorInadimplencia } from './BloqueioPorInadimplencia';
import { BRANDING } from '../config/branding';
import { SimboloCeres } from './Marca';
import { BannerDeCookies } from './BannerDeCookies';
import { AvisoDaAssinatura } from './AvisoDaAssinatura';
import { useSession } from '../lib/session';
import { useMenuColapsado } from '../hooks/useMenuColapsado';
import { IconChevronLeft, IconChevronRight, IconClose, IconMenu, Spinner } from './ui';
import { VersionBadge } from './VersionBadge';
import { MenuDoUsuario } from './MenuDoUsuario';
import { AssistenteDock } from './AssistenteDock';
import { BuscaGlobal } from './experiencia/BuscaGlobal';
import { MenuCriar } from './experiencia/MenuCriar';
import { t } from '../lib/i18n';
import {
  MAIS_NAV,
  NAV_PRINCIPAL,
  estaAtivo,
  itensAutorizados,
  jornadaDoCaminho,
  type Autorizacao,
  type NavItem,
} from './navegacao';

// Compatibilidade: quem importava a lista do AppShell continua encontrando.
export { ITENS_DE_NAVEGACAO, NAV_PRINCIPAL, GRUPOS_DO_MAIS, MAIS_NAV } from './navegacao';

/** O que o filtro do menu precisa da sessão, num lugar só. */
export function useAutorizacao(): Autorizacao {
  const { can, canAny, hasFeature, sessaoPronta } = useSession();
  return { can, canAny, hasFeature, sessaoPronta };
}

/** As entradas visíveis: jornadas autorizadas + o "Mais", que sempre existe. */
function useEntradasDoMenu(): NavItem[] {
  const autorizacao = useAutorizacao();
  return [...itensAutorizados(NAV_PRINCIPAL, autorizacao), MAIS_NAV];
}

/**
 * Dica acessível por mouse E teclado.
 *
 * CSS puro: `group-has-[:focus-visible]` reage ao MESMO foco que chega por Tab.
 * O nome acessível de verdade vem do `aria-label` do próprio link — esta dica é
 * reforço visual, não a única forma de saber o que o ícone é.
 */
function DicaAcessivel({ texto, children }: { texto: string; children: ReactNode }): JSX.Element {
  return (
    <span className="group/dica relative flex">
      {children}
      <span
        role="tooltip"
        aria-hidden="true"
        className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-1 text-xs font-medium text-ink-inverse opacity-0 shadow-pop transition-opacity duration-150 group-hover/dica:opacity-100 group-has-[:focus-visible]/dica:opacity-100"
      >
        {texto}
      </span>
    </span>
  );
}

/**
 * Uma jornada no menu lateral, nos dois estados.
 *
 * `Link`, e não `NavLink`: o "lugar atual" vem do MAPA (`estaAtivo`), que sabe
 * que a ficha da empresa é Relacionamentos e que Comissões mora no Mais. O
 * `NavLink` impõe o próprio `aria-current` pelo caminho literal e desmarcaria
 * a jornada certa.
 */
function ItemDaJornada({ item, recolhido }: { item: NavItem; recolhido: boolean }): JSX.Element {
  const { pathname } = useLocation();
  const ativo = estaAtivo(item, pathname);
  const rotulo = t(item.chave);

  if (recolhido) {
    return (
      <DicaAcessivel texto={rotulo}>
        <Link
          to={item.to}
          aria-label={rotulo}
          aria-current={ativo ? 'page' : undefined}
          className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-brand-100 transition hover:bg-brand-700 hover:text-ink-inverse ${ativo ? 'nav-link-active' : ''}`}
        >
          <item.icon className="h-5 w-5" />
        </Link>
      </DicaAcessivel>
    );
  }

  return (
    <Link
      to={item.to}
      aria-current={ativo ? 'page' : undefined}
      className={`nav-link min-h-[3rem] py-2 ${ativo ? 'nav-link-active' : ''}`}
    >
      <item.icon className={`h-5 w-5 shrink-0 ${ativo ? 'text-accent-300' : ''}`} />
      <span className="grid min-w-0">
        <span className="truncate font-semibold">{rotulo}</span>
        {item.ajuda !== undefined && (
          <span className={`truncate text-2xs font-normal ${ativo ? 'text-brand-100' : 'text-brand-200'}`}>
            {t(item.ajuda)}
          </span>
        )}
      </span>
    </Link>
  );
}

/**
 * O conteúdo da navegação lateral.
 *
 * `variante`:
 * - `'desktop'`: respeita `colapsado` e mostra o botão de recolher/expandir;
 * - `'drawer'`: sempre completo e com botão de fechar.
 */
function ConteudoDaSidebar({
  colapsado,
  alternar,
  variante,
  aoFechar,
  aoAbrirGuia,
  primeiroFocoRef,
}: {
  colapsado: boolean;
  alternar: () => void;
  variante: 'desktop' | 'drawer';
  aoFechar?: () => void;
  aoAbrirGuia: () => void;
  primeiroFocoRef?: React.RefObject<HTMLButtonElement>;
}): JSX.Element {
  const recolhido = variante === 'desktop' && colapsado;
  const jornadas = itensAutorizados(NAV_PRINCIPAL, useAutorizacao());

  return (
    <div className="flex h-full flex-col bg-brand-600">
      <div
        className={`flex h-16 shrink-0 items-center gap-2.5 border-b border-brand-700 px-3 ${
          recolhido ? 'justify-center px-2' : ''
        }`}
      >
        {!recolhido && <SimboloCeres className="h-8 w-8 shrink-0" variante="white" decorativa />}
        {!recolhido && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold tracking-wide text-ink-inverse">
              {BRANDING.productName}
            </p>
            <p className="truncate text-2xs text-brand-300">{BRANDING.tagline}</p>
          </div>
        )}

        {variante === 'desktop' ? (
          <DicaAcessivel texto={colapsado ? t('nav.expandirMenu') : t('nav.recolherMenu')}>
            <button
              ref={primeiroFocoRef}
              type="button"
              onClick={alternar}
              aria-pressed={colapsado}
              aria-label={colapsado ? t('nav.expandirMenu') : t('nav.recolherMenu')}
              className={`flex shrink-0 items-center justify-center rounded-lg text-brand-200 hover:bg-brand-700 hover:text-ink-inverse ${
                recolhido ? 'h-11 w-11' : 'h-8 w-8'
              }`}
            >
              {recolhido ? (
                <span className="relative flex items-center">
                  <SimboloCeres className="h-7 w-7" variante="white" decorativa />
                  <IconChevronRight className="absolute -right-2.5 h-3.5 w-3.5 text-accent-300" />
                </span>
              ) : (
                <IconChevronLeft className="h-4 w-4" />
              )}
            </button>
          </DicaAcessivel>
        ) : (
          <button
            ref={primeiroFocoRef}
            type="button"
            onClick={aoFechar}
            aria-label={t('nav.fecharMenu')}
            className="ml-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-brand-200 hover:bg-brand-700 hover:text-ink-inverse"
          >
            <IconClose className="h-4 w-4" />
          </button>
        )}
      </div>

      <nav
        aria-label={t('nav.navegacaoPrincipal')}
        className={`flex flex-1 flex-col py-4 ${recolhido ? 'items-center gap-1.5 overflow-visible px-2' : 'gap-1 overflow-y-auto scroll-slim px-2'}`}
      >
        {jornadas.map((item) => (
          <ItemDaJornada key={item.to} item={item} recolhido={recolhido} />
        ))}
        <div className={`mt-auto border-t border-brand-700 pt-2 ${recolhido ? 'flex justify-center' : ''}`}>
          <ItemDaJornada item={MAIS_NAV} recolhido={recolhido} />
        </div>
      </nav>

      <div className="border-t border-brand-700 p-2" data-rodape-da-navegacao="">
        <MenuDoUsuario aoAbrirGuia={aoAbrirGuia} colapsado={recolhido} />
        {!recolhido && <VersionBadge />}
      </div>
    </div>
  );
}

/**
 * A BARRA SUPERIOR (desktop): onde a pessoa está, a busca global e o "+ Criar".
 *
 * O nome da jornada vem do mesmo mapa do menu — nunca um título escrito à
 * parte, que envelheceria na primeira rota nova.
 */
function BarraSuperior(): JSX.Element {
  const { pathname } = useLocation();
  const jornada = jornadaDoCaminho(pathname);

  return (
    <header className="sticky top-0 z-20 hidden h-16 items-center justify-between gap-4 border-b border-line bg-surface/95 px-6 backdrop-blur lg:flex">
      <div className="min-w-0">
        {jornada !== null && (
          <>
            <p className="truncate text-sm font-bold text-brand-600">{t(jornada.chave)}</p>
            {jornada.ajuda !== undefined && (
              <p className="truncate text-2xs text-ink-subtle">{t(jornada.ajuda)}</p>
            )}
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <BuscaGlobal />
        <MenuCriar />
      </div>
    </header>
  );
}

/**
 * NAVEGAÇÃO INFERIOR (celular e tablet estreito).
 *
 * As seis entradas, com ícone e nome curto — nome sempre visível, porque ícone
 * sozinho é adivinhação. Alvos de 52px de altura.
 */
function NavegacaoInferior(): JSX.Element {
  const entradas = useEntradasDoMenu();
  const { pathname } = useLocation();
  return (
    <nav
      aria-label={t('ux.nav.navegacaoInferior')}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-brand-700 bg-brand-600 pb-[env(safe-area-inset-bottom)] lg:hidden"
    >
      <ul className="mx-auto flex max-w-xl items-stretch justify-around">
        {entradas.map((item) => {
          const ativo = estaAtivo(item, pathname);
          return (
            <li key={item.to} className="min-w-0 flex-1">
              <Link
                to={item.to}
                aria-current={ativo ? 'page' : undefined}
                className={`relative flex min-h-[3.25rem] flex-col items-center justify-center gap-0.5 px-1 text-[0.625rem] font-medium ${
                  ativo ? 'text-accent-300' : 'text-brand-100'
                }`}
              >
                {ativo && (
                  <span aria-hidden="true" className="absolute inset-x-4 top-0 h-[3px] rounded-b bg-accent-300" />
                )}
                <item.icon className="h-5 w-5" />
                <span className="max-w-full truncate">{t(item.chave)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function AppShell(): JSX.Element {
  const { user, can, hasFeature } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { colapsado, alternar } = useMenuColapsado(user?.organizationId ?? '', user?.id ?? '');

  const botaoAbrirDrawerRef = useRef<HTMLButtonElement>(null);
  const primeiroFocoDoDrawerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Fecha a gaveta ao navegar — no tablet ela cobre o conteúdo.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const fecharDrawer = (): void => {
    setDrawerOpen(false);
    botaoAbrirDrawerRef.current?.focus();
  };

  /**
   * FOCO CONTROLADO DA GAVETA (mobile/tablet).
   *
   * Ao abrir, o foco vai para dentro da gaveta; ao fechar, volta para o botão
   * que abriu. Tab circula dentro dela e Escape fecha.
   */
  useEffect(() => {
    if (!drawerOpen) return undefined;
    primeiroFocoDoDrawerRef.current?.focus();

    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        fecharDrawer();
        return;
      }
      if (e.key !== 'Tab' || drawerRef.current === null) return;
      const focaveis = drawerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (primeiro === undefined || ultimo === undefined) return;
      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [drawerOpen]);

  return (
    /*
     * `--altura-nav-inferior` (CERES-IA-002): quanto a navegação inferior ocupa
     * no rodapé — zero a partir de lg, onde ela não existe. O acionador do
     * assistente e a faixa de cookies sobem por ela, em vez de ficarem embaixo.
     */
    <div className="flex min-h-screen bg-surface-canvas [--altura-nav-inferior:calc(3.25rem+1px+env(safe-area-inset-bottom))] lg:[--altura-nav-inferior:0px]">
      {/* Navegação fixa a partir de lg — largura muda com a preferência. */}
      <aside
        className={`relative z-30 hidden shrink-0 bg-brand-600 transition-[width] duration-200 motion-reduce:transition-none lg:block ${
          colapsado ? 'w-[72px]' : 'w-[248px]'
        }`}
      >
        <div className="sticky top-0 h-screen">
          <ConteudoDaSidebar
            colapsado={colapsado}
            alternar={alternar}
            variante="desktop"
            aoAbrirGuia={() => navigate('/?guia=1')}
          />
        </div>
      </aside>

      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-surface-overlay lg:hidden"
            onClick={fecharDrawer}
            aria-hidden="true"
          />
          <aside
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.navegacaoPrincipal')}
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-brand-600 lg:hidden"
          >
            <ConteudoDaSidebar
              colapsado={false}
              alternar={alternar}
              variante="drawer"
              aoFechar={fecharDrawer}
              aoAbrirGuia={() => {
                fecharDrawer();
                navigate('/?guia=1');
              }}
              primeiroFocoRef={primeiroFocoDoDrawerRef}
            />
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-brand-700 bg-brand-600 px-4 lg:hidden">
          <button
            ref={botaoAbrirDrawerRef}
            type="button"
            className="btn btn-sm h-10 w-10 border-brand-400 bg-brand-700 text-ink-inverse hover:bg-brand-800"
            onClick={() => setDrawerOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
            aria-label={t('nav.abrirNavegacao')}
          >
            <IconMenu className="h-4 w-4" />
          </button>
          <SimboloCeres className="h-6 w-6" variante="white" decorativa />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink-inverse">
            {BRANDING.productName}
          </span>
          <BuscaGlobal compacta />
        </header>

        <BarraSuperior />

        {/* O rodapé reserva a navegação inferior, a faixa de cookies e o
            acionador do assistente: o fim da página nunca fica embaixo deles. */}
        <main
          className="min-w-0 flex-1 px-4 pt-4 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6"
          // Em linha: vence a reserva fixa de `styles.css` (8,5rem com a faixa
          // visível), que não somava a navegação inferior.
          style={{ paddingBottom: 'calc(var(--altura-nav-inferior, 0px) + var(--altura-banner-cookies, 0px) + 4.5rem)' }}
        >
          {/*
            A ESPERA FICA AQUI, e não em volta da aplicação inteira: o menu e a
            barra continuam na tela enquanto o miolo carrega.
          */}
          <Suspense
            fallback={
              <div className="flex items-center gap-2 p-6 text-sm text-ink-muted" role="status">
                <Spinner className="h-4 w-4" />
                {t('estado.carregando')}
              </div>
            }
          >
            {user?.contexto !== 'PLATFORM' && <AvisoDaAssinatura organizationId={user?.organizationId ?? ''} />}
            <Outlet />
            <BloqueioPorInadimplencia />
          </Suspense>
        </main>

        <BannerDeCookies />

        {/* Capacidade do plano E permissão da pessoa: sem `agent:use` a API
            recusa a conversa, e o botão levaria a um painel que não responde. */}
        {hasFeature('ai_assistant') && can('agent:use') && <AssistenteDock />}
      </div>

      <NavegacaoInferior />
    </div>
  );
}
